import { useCallback, useRef, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useMst } from "../../stores/main";
import { frameToPixel, pixelToFrame } from "./utils/timelineHelpers";
import { recordStudioPerf, studioPerfNow } from "../../helpers/studioPerfDebug";

interface TimelinePlayheadProps {
  compositionStart: number;
  totalFrames: number;
  onSeek: (frame: number) => void;
  variant?: "full" | "head" | "line";
}

export const TimelinePlayhead = observer(
  ({
    compositionStart,
    totalFrames,
    onSeek,
    variant = "full",
  }: TimelinePlayheadProps) => {
    const renderStartedAt = studioPerfNow();
    const {
      playerStore: { currentFrame },
      timelineStore: { zoom, scrollOffset },
    } = useMst();

    const isDragging = useRef(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Adjust frame relative to composition start
    const relativeFrame = currentFrame - compositionStart;
    const x = frameToPixel(relativeFrame, zoom, scrollOffset);

    const calculateFrameFromMouseEvent = useCallback(
      (clientX: number): number => {
        // Get the tracks area to calculate position relative to it
        // Use timeline-tracks-area as reference since it works for both head and line variants
        const tracksArea =
          containerRef.current?.closest(".timeline-tracks__content") ||
          containerRef.current?.closest(".timeline-tracks-area");
        if (!tracksArea) return currentFrame;

        const rect = tracksArea.getBoundingClientRect();
        const mouseX = clientX - rect.left;
        const frame =
          pixelToFrame(mouseX, zoom, scrollOffset) + compositionStart;

        // Clamp to valid frame range
        return Math.max(
          compositionStart,
          Math.min(compositionStart + totalFrames, frame)
        );
      },
      [zoom, scrollOffset, compositionStart, totalFrames, currentFrame]
    );

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      isDragging.current = true;
      document.body.style.cursor = "ew-resize";
      document.body.style.userSelect = "none";
    }, []);

    useEffect(() => {
      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging.current) return;
        const frame = calculateFrameFromMouseEvent(e.clientX);
        onSeek(frame);
      };

      const handleMouseUp = () => {
        if (isDragging.current) {
          isDragging.current = false;
          document.body.style.cursor = "";
          document.body.style.userSelect = "";
        }
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }, [calculateFrameFromMouseEvent, onSeek]);

    // Don't render if off-screen
    if (x < 0 || x > 3000) {
      recordStudioPerf(
        "TimelinePlayhead render offscreen",
        studioPerfNow() - renderStartedAt,
        {
          currentFrame: Math.round(currentFrame),
          totalFrames,
          variant,
          x: Math.round(x),
        }
      );
      return null;
    }

    const showHead = variant === "full" || variant === "head";
    const showLine = variant === "full" || variant === "line";

    const classNames = [
      "timeline-playhead",
      variant === "head" && "timeline-playhead--head-only",
      variant === "line" && "timeline-playhead--line-only",
    ]
      .filter(Boolean)
      .join(" ");

    recordStudioPerf(
      "TimelinePlayhead render",
      studioPerfNow() - renderStartedAt,
      {
        currentFrame: Math.round(currentFrame),
        totalFrames,
        variant,
        x: Math.round(x),
      }
    );

    return (
      <div className={classNames} style={{ left: x }} ref={containerRef}>
        {showHead && (
          <div
            className="timeline-playhead__head"
            onMouseDown={handleMouseDown}
          />
        )}
        {showLine && <div className="timeline-playhead__line" />}
      </div>
    );
  }
);
